/**
 * Orchestrator command-line arguments.
 *
 * Pool backends carry a single free-form `orchestratorArgs` string — the verbatim
 * CLI arguments appended to the orc-orchestrator launch on each worker. This
 * replaced the old set of typed boolean/string "orchestrator flag" knobs.
 *
 * The helpers here convert the legacy typed flags into the equivalent
 * `orchestratorArgs` string so existing pool configs migrate losslessly, and
 * resolve the effective args for a backend (preferring an explicit
 * `orchestratorArgs`, falling back to the legacy flags).
 *
 * Both the console (editing pool config) and the gateway (generating startup
 * scripts) import these so the conversion can never drift between the two.
 */
/** Wrap a flag value in double quotes only when it contains whitespace. */
function quoteArg(value) {
    return /\s/.test(value) ? `"${value}"` : value;
}
/**
 * Collapse a free-form args string (which may be multi-line, from a textarea)
 * into a single clean command line: every whitespace run — newlines, tabs, and
 * repeated spaces — becomes a single space, and leading/trailing whitespace is
 * stripped. Extra spaces between args are insignificant to CLI tokenization.
 */
export function normalizeOrchestratorArgs(raw) {
    return raw.replace(/\s+/g, " ").trim();
}
/**
 * Convert the legacy typed orchestrator flags into the equivalent free-form
 * CLI args string. Active flags first, then deprecated flags (kept so pools
 * targeting earlier orc versions retain their behavior after migration).
 */
export function legacyFlagsToOrchestratorArgs(flags) {
    const args = [];
    // Active orc CLI flags.
    if (flags.ultraFastSlots)
        args.push("--ultra-fast-slots");
    if (flags.profilePool)
        args.push("--profile-pool");
    if (flags.noJobBoard)
        args.push("--job-board", "false");
    if (flags.uiIsolation)
        args.push("--ui-isolation");
    if (flags.logJsonPath)
        args.push("--log-json-path", quoteArg(flags.logJsonPath));
    if (flags.logLevel)
        args.push("--log-level", quoteArg(flags.logLevel));
    // Deprecated flags — preserved for backwards compatibility with earlier orc
    // builds. Modern orc rejects unknown flags, so trim these on current builds.
    if (flags.simpleSlots)
        args.push("--simple-slots");
    if (flags.shareWorkspace)
        args.push("--share-workspace");
    if (flags.noWarm)
        args.push("--no-warm");
    if (flags.instantOn)
        args.push("--instant-on");
    if (flags.precache)
        args.push("--precache");
    if (flags.lazyDownloads)
        args.push("--lazy-downloads");
    return args.join(" ");
}
/**
 * Resolve the effective orchestrator args for a backend: an explicit
 * `orchestratorArgs` (even empty, which means "no extra args") wins; otherwise
 * derive from the legacy typed flags for configs saved before the migration.
 */
export function resolveOrchestratorArgs(source) {
    if (source.orchestratorArgs !== undefined) {
        return normalizeOrchestratorArgs(source.orchestratorArgs);
    }
    return legacyFlagsToOrchestratorArgs(source);
}
